<script setup lang="ts">
import { ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import useDialog from "@/ui/composables/useDialog";
import UserUploadedFileDTO = App.Data.Explorer.Filter.UserUploadedFileDTO;

const props = defineProps<{
	fileName: string;
	fileUuid: string;
	locked: boolean;
}>();

/** null: nothing is attached any more, so the draft moves to a fresh slot. */
const emit = defineEmits<{
	update: [file: UserUploadedFileDTO | null];
}>();

const selectedFile = ref<File>();
const isUploading = ref(false);
const uploadProgress = ref(0);
const errorMessage = ref("");

const validateFileContent = async (file: File): Promise<boolean> => {
	return new Promise((resolve) => {
		const reader = new FileReader();

		reader.onload = (event) => {
			const content = event.target?.result as string;
			if (!content) {
				resolve(false);
				return;
			}

			// Get the first line
			const lines = content.split("\n");
			const firstLine = lines[0]?.trim();

			// Check if first line is exactly "id"
			resolve(firstLine.replaceAll(/[^a-z0-9\-_]/gi, "") === "id");
		};

		reader.onerror = () => {
			resolve(false);
		};

		// Read only the first few bytes to check the first line
		// This is more efficient for large files
		const blob = file.slice(0, 1024); // Read first 1KB
		reader.readAsText(blob);
	});
};

const resetUploadState = (): void => {
	isUploading.value = false;
	uploadProgress.value = 0;
	errorMessage.value = "";
};

const clearFileInput = (): void => {
	resetUploadState();
};

/**
 * A new uuid means a different file slot, so drop whatever the input still
 * holds. The error stays on screen: a rekey is how a failed upload is recovered
 * from, and the next pick clears it anyway.
 */
watch(
	() => props.fileUuid,
	() => {
		selectedFile.value = undefined;
		isUploading.value = false;
		uploadProgress.value = 0;
	}
);

const onFileChange = async () => {
	if (!selectedFile.value) {
		return;
	}

	resetUploadState();

	const isValid = await validateFileContent(selectedFile.value);
	if (isValid) {
		await uploadFile(selectedFile.value);
	} else {
		errorMessage.value =
			'The selected file is not valid. It should be a CSV file which has one column with the header "id".';
	}
};

const uploadFile = async (file: File | null) => {
	isUploading.value = true;

	uploadProgress.value = 0;

	try {
		const response = await useApiClient("files").store(
			props.fileUuid,
			file,
			(progressEvent) => {
				if (progressEvent.total) {
					uploadProgress.value = Math.round(
						(progressEvent.loaded * 100) / progressEvent.total
					);
				}
			}
		);

		isUploading.value = false;

		if (response.isOk()) {
			emit("update", response.value);
		} else if (response.isErr()) {
			errorMessage.value = response.error.message;
			emit("update", null);
		}
	} catch (error) {
		errorMessage.value =
			error.response?.data?.message || "Upload failed. Please try again.";
		emit("update", null);
	}
};

const isDownloading = ref(false);
const downloadFile = async () => {
	if (isDownloading.value) {
		return;
	}

	isDownloading.value = true;
	const response = await useApiClient("files").getDownloadUrl(props.fileUuid);
	isDownloading.value = false;

	if (response.isOk()) {
		const link = document.createElement("a");
		link.href = response.value.url;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} else if (response.isErr()) {
		const dialog = useDialog();
		await dialog.alert({
			title: "Download failed",
			message: response.error.message,
		});
	}
};
</script>

<template>
	<div>
		<v-chip
			v-if="locked"
			closable
			color="primary"
			text-color="white"
			variant="tonal"
			class="mt-2"
			@click:close="emit('update', null)"
			:disabled="isDownloading"
			@click="downloadFile"
		>
			<v-icon left small class="mr-2">mdi-file</v-icon>
			{{ fileName }}
		</v-chip>
		<template v-else>
			<v-file-input
				v-model="selectedFile"
				:disabled="isUploading"
				@change="onFileChange"
				@click:clear="clearFileInput"
			/>
			<v-progress-linear
				v-if="isUploading"
				:value="uploadProgress"
				color="primary"
				height="6"
				class="mt-2"
			></v-progress-linear>
		</template>
		<v-alert v-if="errorMessage" type="error" dense class="mt-2">
			{{ errorMessage }}
		</v-alert>
	</div>
</template>

<style scoped lang="scss"></style>
